import React, { useCallback, useEffect, useState } from "react";
import {
	Grid,
	Box,
	IconButton,
	TextField,
	Autocomplete,
	useMediaQuery,
	useTheme,
	InputAdornment,
	Typography,
} from "@mui/material";
import { Clear, Print as PrintIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { connect } from "react-redux";
import debounce from "lodash.debounce";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PrintsSearch3 from "../../../components/PrintSearch3";
import {
	areAllPropsEmpty,
	getDaysUntil,
	renderCodeOption,
} from "../../../utils/functions";
import CSVLinkWrapper from "../../../components/CSVLinkWrapper";
import PATClearButton from "../../../components/Buttons/PATClearButton";
import FilterToolbar from "../../../components/FilterToolbar";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import FilterButtons from "../../../components/Buttons/FilterButtons";
import { screenNames } from "../../../utils/constants";
import SelectFilterDialog from "../../../components/SelectFilterDialog";
import SaveFilterDialog from "../../../components/SaveFilterDialog";
import { formatSaveDateFrom, formatSaveDateTo } from "../../../reducers/TimeReducer";

const headers = [
	{ key: "dateObtained", label: "Obtained", format: "date" },
	{ key: "certificationType", label: "Type" },
	{ key: "employeeName", label: "Employee" },
	{ key: "expireDate", label: "Expiration", format: "date" },
	{ key: "daysUntil", label: "Days Until" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
	...h,
}));

const CertificationTableToolbar = ({
	certifications,
	keyboard,
	onAddClick,
	onFilterChange,
	filters,
	resetFilters,
	employeeOptions,
	certificationTypeOptions,
	employmentStatusOptions,
	tagTypeOptions,
}) => {
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const [printFlag, setPrintFlag] = useState(false);
	const [kbdKey, setKbdKey] = useState(null);
	const [localFilters, setLocalFilters] = useState(filters);
	const [transformedCertifications, setTransformedCertifications] = useState(
		[]
	);
	const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
	const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);

	const isFilterEmpty = areAllPropsEmpty(localFilters);

	const certificatePermission = getAccessPermission(
		"training",
		"Certifications",
		"any"
	);

	const canAddCertificate = !!certificatePermission?.Create;

	useEffect(() => {
		const transformed = (certifications || []).map((cert) => {
			const details = cert?.employeeDetails;

			let employeeName = "";
			if (cert.employee && details && typeof details === "object") {
				const firstName = details.firstName || "";
				const lastName = details.lastName || "";
				const middleName = details.middleName || "";

				employeeName = [lastName, firstName, middleName]
					.filter((n) => typeof n === "string" && n.trim() !== "")
					.join(" ")
					.trim();
			}

			return {
				...cert,
				daysUntil: getDaysUntil(cert.expireDate),
				employeeName,
			};
		});

		setTransformedCertifications(transformed);
	}, [certifications]);

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange]
	);

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters]
	);

	useEffect(() => {
		const subscription = keyboard.subject.subscribe((key) => {
			const validKeys = ["F5", "Ctrl+KeyP", "Ctrl+KeyL"];
			if (validKeys.includes(key)) setKbdKey(key);
		});
		return () => subscription.unsubscribe();
	}, [keyboard]);

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" && (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					)}

					{type === "date" && (
						<DatePicker
							label={label}
							value={
								localFilters[field]
									? moment(localFilters[field])
									: null
							}
							onChange={(date) =>
								handleFilterChange(
									field,
									date
										? ((field === "dateObtainedFrom" || field === "expireDateFrom") ? formatSaveDateFrom(date) : formatSaveDateTo(date))
										: null
								)
							}
							minDate={
								field === "dateObtainedTo" &&
								localFilters.dateObtainedFrom
									? moment(localFilters.dateObtainedFrom)
									: field === "expireDateTo" &&
									  localFilters.expireDateFrom
									? moment(localFilters.expireDateFrom)
									: undefined
							}
							maxDate={
								field === "dateObtainedFrom" &&
								localFilters.dateObtainedTo
									? moment(localFilters.dateObtainedTo)
									: field === "expireDateFrom" &&
									  localFilters.expireDateTo
									? moment(localFilters.expireDateTo)
									: undefined
							}
							clearable
							clearText="Clear"
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									size="small"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{localFilters[field] && (
													<InputAdornment position="end">
														<IconButton
															onClick={() =>
																handleFilterChange(
																	field,
																	null
																)
															}
															size="small"
															edge="end"
														>
															<Clear fontSize="small" />
														</IconButton>
													</InputAdornment>
												)}
												{
													params.InputProps
														?.endAdornment
												}
											</>
										),
									}}
								/>
							)}
						/>
					)}

					{type !== "text" && type !== "date" && (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderOption={renderCodeOption}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
									  }
									: {}
							}
						/>
					)}
				</Grid>
			)
		);

	const renderOptions = () => {
		return (
			<>
				{isSmall ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid item>
							<CSVLinkWrapper
								data={transformedCertifications}
								headers={headers}
								filename="certifications.csv"
							/>
						</Grid>
						<Grid item>
							<IconButton
								onClick={handlePrintClick}
								color="primary"
								disabled={!transformedCertifications?.length}
							>
								<PrintIcon />
							</IconButton>
						</Grid>
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							width: "100%",
						}}
					>
						<Grid item>
							<CSVLinkWrapper
								data={transformedCertifications}
								headers={headers}
								filename="certifications.csv"
							/>
						</Grid>
						<Grid item>
							<IconButton
								onClick={handlePrintClick}
								color="primary"
								disabled={!transformedCertifications?.length}
							>
								<PrintIcon />
							</IconButton>
						</Grid>
					</Box>
				)}
			</>
		);
	};

	const renderAddButton = () => {
		return (
			<>
				{canAddCertificate && (
					<Grid item>
						<PATAddButton
							tooltipText="Add Certificate"
							onClick={onAddClick}
						/>
					</Grid>
				)}
			</>
		);
	};

	const renderClearButton = () => {
		return (
			<>
				<Grid item>
					<Grid item>
						<PATClearButton
							title="Clear Filters"
							onClick={resetFilters}
						/>
					</Grid>
				</Grid>
			</>
		);
	};

	const filterFields = [
		{
			type: "date",
			label: "Date Obtained From",
			field: "dateObtainedFrom",
			xs: 3,
		},
		{
			type: "date",
			label: "Date Obtained To",
			field: "dateObtainedTo",
			xs: 3,
		},
		{
			type: "autocomplete",
			label: "Employee",
			field: "employee",
			xs: 3,
			options: employeeOptions,
			multiselect: false,
		},
		{
			type: "autocomplete",
			label: "Certification Type",
			field: "certificationType",
			xs: 3,
			options: certificationTypeOptions,
			multiselect: false,
		},
		{
			type: "autocomplete",
			label: "Tags",
			field: "tagTypes",
			options: tagTypeOptions,
			xs: 2.6,
		},
		{
			type: "autocomplete",
			label: "Employment Status",
			field: "employeeStatus",
			xs: 3,
			options: employmentStatusOptions,
			multiselect: false,
		},
		{
			type: "date",
			label: "Expiration From",
			field: "expireDateFrom",
			xs: 3,
		},
		{ type: "date", label: "Expiration To", field: "expireDateTo", xs: 3 },
	];

	return (
		<>
			<Typography sx={{ fontSize: 20 }}>Certifications</Typography>
			<FilterToolbar
				isSmall={isSmall}
				canCreate={canAddCertificate}
				leftSection={
					!isSmall && (
						<Box
							sx={{
								border: (theme) =>
									`1px solid ${theme.palette.primary.main}`,
								borderRadius: "10px",
								padding: 2,
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								marginLeft: 1,
							}}
						>
							{renderAddButton()}
						</Box>
					)
				}
				middleSection={
					<Box
						sx={{
							border: (theme) =>
								`1px solid ${theme.palette.primary.main}`,
							borderRadius: "10px",
							padding: 2,
							maxHeight: { xs: "25vh", md: "40vh" },
							overflowY: "auto",
							marginLeft: canAddCertificate ? "" : 1.5,
						}}
					>
						<Grid container spacing={1}>
							{renderFilterFields([...filterFields])}
							{renderClearButton()}
							<FilterButtons
								onSelectFilterClick={() =>
									setOpenSelectFilterDialog(true)
								}
								onSaveFilterClick={() =>
									setOpenSaveFilterDialog(true)
								}
								isFilterEmpty={isFilterEmpty}
							/>
						</Grid>
					</Box>
				}
				rightSection={
					!isSmall && (
						<Box
							sx={{
								border: (theme) =>
									`1px solid ${theme.palette.primary.main}`,
								borderRadius: "10px",
								padding: 2,
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: 1,
								height: "100%",
								justifyContent: "center",
								marginRight: 1,
							}}
						>
							{renderOptions()}
						</Box>
					)
				}
				combinedActions={
					<Box
						sx={{
							mt: 2,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 2,
							flexWrap: "wrap",
						}}
					>
						{renderAddButton()}
						{renderOptions()}
					</Box>
				}
				leftGridProps={{ xs: 12, md: 1 }}
				middleGridProps={{ xs: 12, md: 10 }}
				rightGridProps={{ xs: 12, md: 1 }}
			/>

			<PrintsSearch3
				title="Certifications"
				data={transformedCertifications}
				cols={columnsForPrint}
				kbdKey={kbdKey}
				print={printFlag}
			/>

			<SelectFilterDialog
				open={openSelectFilterDialog}
				onClose={() => setOpenSelectFilterDialog(false)}
				screenName={screenNames.certifications}
				onFilterChange={onFilterChange}
			/>

			<SaveFilterDialog
				open={openSaveFilterDialog}
				onClose={() => setOpenSaveFilterDialog(false)}
				screenName={screenNames.certifications}
				filters={localFilters}
			/>
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	keyboard: state.keyboard,
	...ownProps,
});

export default connect(mapStateToProps)(CertificationTableToolbar);
